/**
 * PSV-1890 — fullscreen .io-game entry overlay (name + PLAY).
 *
 * Plain DOM, NO Phaser: a text input inside a Phaser scene is painful
 * (IME/selection/focus quirks, DOMElement plumbing), so the overlay is a
 * position:fixed layer ABOVE the canvas. The Phaser scene keeps rendering
 * the starfield behind it — the background stays alive while the
 * handshake loads.
 *
 * Shown on boot (and re-shown from the death recap's "Change name"), so
 * hide() only sets display:none and keeps the tree in the DOM; destroy()
 * removes it for real.
 *
 * Name validation here is UX only — the SERVER re-validates the pilot
 * name authoritatively. `sanitizePilotName` is the client mirror of the
 * server rule (trim, collapse spaces, 3–16 chars, Latin/Cyrillic letters,
 * digits, space, `_` `.` `-`) and is exported for reuse by
 * ArenaController and friends.
 *
 * Keyboard capture: the game's key bindings listen on window, so while
 * the overlay is visible a capture-phase keydown listener on document
 * stops propagation of ALL keydown (WASD/M never leak into the scene).
 * Escape is let through untouched. Because the capture stop also
 * prevents the event from reaching the input's own listeners, Enter
 * (= click PLAY) is handled inside that same capture handler.
 */

/** Allowed characters after trim/collapse — mirror of the server rule. */
const NAME_CHARS_RE = /^[\p{Script=Latin}\p{Script=Cyrillic}0-9 _.-]+$/u;

const NAME_MIN = 3;
const NAME_MAX = 16;

/**
 * Client mirror of the server's pilot-name rule (UX only — the server
 * re-validates). Trims, collapses runs of whitespace to single spaces,
 * then requires 3–16 chars from the allowed set. Returns the sanitized
 * name, or null when invalid.
 */
export function sanitizePilotName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name.length < NAME_MIN || name.length > NAME_MAX) return null;
  if (!NAME_CHARS_RE.test(name)) return null;
  return name;
}

const HINT_TEXT = 'Name: 3–16 chars, letters/digits/space/_-.';
// PSV-1902 UX — the entry screen is the ONLY onboarding surface; it must
// explain the goal, not just the keys (PO feedback 2026-07-27).
const GOAL_TEXT =
  'Mine crystals from asteroids · upgrade your ship · hunt the bounty leader';
const CONTROLS_TEXT =
  'WASD / ARROWS — move · MOUSE / SPACE — fire\n' +
  '1-8 — stat upgrades · B — shop · Q — quests · ENTER — chat · M — mute';

const STYLE = `
  .sb-entry-root {
    position: fixed;
    inset: 0;
    /* PSV-1911 QA — in-app browsers: cap to the DYNAMIC viewport (dvh
       tracks collapsing chrome) and scroll when the column is taller
       than a short landscape phone; centered flex alone clips BOTH ends
       (the logo was cut at the top). safe-area for the notch. */
    max-height: 100dvh;
    overflow-y: auto;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    justify-content: safe center;
    gap: 18px;
    padding: max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right))
      max(12px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
    box-sizing: border-box;
    background: radial-gradient(
      ellipse at center,
      rgba(4, 8, 18, 0.82) 0%,
      rgba(4, 8, 18, 0.95) 100%
    );
    backdrop-filter: blur(2px);
    font-family: ui-monospace, Menlo, Consolas, monospace;
  }
  .sb-entry-logo {
    font-size: clamp(40px, 8vw, 72px);
    letter-spacing: 0.35em;
    color: #eaf6ff;
    text-shadow:
      0 0 8px rgba(53, 196, 240, 0.9),
      0 0 24px rgba(53, 196, 240, 0.55),
      0 0 64px rgba(53, 196, 240, 0.3);
    /* letter-spacing trails the last glyph — nudge back to true center */
    margin-right: -0.35em;
    user-select: none;
  }
  .sb-entry-subtitle {
    font-size: 12px;
    letter-spacing: 0.5em;
    margin-right: -0.5em;
    color: #7fd6ea;
    user-select: none;
  }
  .sb-entry-input {
    width: min(320px, 80vw);
    box-sizing: border-box;
    padding: 12px;
    text-align: center;
    font-family: inherit;
    font-size: 18px;
    color: #eaf6ff;
    background: #0b101c;
    border: 1px solid #2c3a5c;
    border-radius: 8px;
    outline: none;
  }
  .sb-entry-input:focus {
    border-color: #35c4f0;
    box-shadow: 0 0 10px rgba(53, 196, 240, 0.35);
  }
  .sb-entry-input::placeholder {
    color: #4a5877;
  }
  .sb-entry-hint {
    font-size: 12px;
    color: #ff5577;
    visibility: hidden;
  }
  .sb-entry-hint--visible {
    visibility: visible;
  }
  .sb-entry-play {
    min-width: 200px;
    padding: 14px;
    font-family: inherit;
    font-size: 18px;
    font-weight: bold;
    letter-spacing: 0.2em;
    color: #04140a;
    background: #44ff7a;
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    transition: transform 0.08s ease, background-color 0.08s ease;
  }
  .sb-entry-play:hover:not(:disabled) {
    background: #6dff98;
    transform: scale(1.03);
  }
  .sb-entry-play:disabled {
    background: #3a3a3a;
    color: #7a7a7a;
    cursor: default;
  }
  .sb-entry-season {
    font-size: 12px;
    color: #ffd84a;
    text-align: center;
    min-height: 14px;
    user-select: none;
  }
  .sb-entry-goal {
    font-size: 13px;
    color: #cfe3f5;
    text-align: center;
    max-width: min(520px, 86vw);
    user-select: none;
  }
  .sb-entry-controls {
    white-space: pre-line;
    line-height: 1.7;
    font-size: 12px;
    color: #9aa0b4;
    text-align: center;
    user-select: none;
  }
  .sb-entry-shake {
    animation: sb-entry-shake 0.3s ease;
  }
  @keyframes sb-entry-shake {
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-8px); }
    40% { transform: translateX(8px); }
    60% { transform: translateX(-5px); }
    80% { transform: translateX(5px); }
  }
`;

export interface EntryOverlayOptions {
  initialName: string;
  onPlay: (name: string) => void;
}

export class EntryOverlay {
  private readonly opts: EntryOverlayOptions;

  private root: HTMLDivElement | null = null;
  private styleEl: HTMLStyleElement | null = null;
  private input: HTMLInputElement | null = null;
  private hint: HTMLDivElement | null = null;
  private seasonEl: HTMLDivElement | null = null;
  private playBtn: HTMLButtonElement | null = null;

  private visible = false;
  private destroyed = false;
  private shakeTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Capture-phase keydown guard (attached to document while the overlay
   * lives): while visible, no keydown escapes to the window-level game
   * bindings — except Escape, which passes through untouched.
   */
  private readonly onKeydownCapture = (event: KeyboardEvent): void => {
    if (!this.visible || this.destroyed) return;
    if (event.key === 'Escape') return;
    event.stopPropagation();
    // The capture stop prevents the event from ever reaching the input's
    // own listeners, so Enter (= click PLAY) is handled right here.
    if (event.key === 'Enter') this.submit();
  };

  constructor(opts: EntryOverlayOptions) {
    this.opts = opts;
  }

  /** Builds the DOM (once) and displays it; focuses + selects the input. */
  show(): void {
    if (this.destroyed) return;
    if (this.root === null) this.build();
    this.visible = true;
    this.root!.style.display = 'flex';
    if (this.input !== null) {
      this.input.focus();
      this.input.select();
    }
  }

  /** display:none — kept in the DOM for re-show ("Change name" path). */
  hide(): void {
    if (this.destroyed) return;
    this.visible = false;
    if (this.root !== null) this.root.style.display = 'none';
  }

  /** PSV-1906 — season line under the goal text (set async after load). */
  setSeasonLine(text: string): void {
    if (this.destroyed || this.seasonEl === null) return;
    this.seasonEl.textContent = text;
  }

  /** Removes the overlay from the DOM. Idempotent. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.visible = false;
    document.removeEventListener('keydown', this.onKeydownCapture, true);
    if (this.shakeTimer !== null) {
      clearTimeout(this.shakeTimer);
      this.shakeTimer = null;
    }
    this.root?.remove();
    this.styleEl?.remove();
    this.root = null;
    this.styleEl = null;
    this.input = null;
    this.hint = null;
    this.playBtn = null;
  }

  // ---------- private ----------

  private build(): void {
    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLE;
    document.head.appendChild(this.styleEl);

    this.root = document.createElement('div');
    this.root.className = 'sb-entry-root';
    this.root.style.display = 'none';

    const logo = document.createElement('div');
    logo.className = 'sb-entry-logo';
    // PSV-1905 renaming: «Starblast» collides with the existing starblast.io.
    logo.textContent = 'STARSPILL';

    const subtitle = document.createElement('div');
    subtitle.className = 'sb-entry-subtitle';
    subtitle.textContent = 'A PLAYSERV DEMO';

    this.input = document.createElement('input');
    this.input.className = 'sb-entry-input';
    this.input.type = 'text';
    this.input.maxLength = NAME_MAX;
    this.input.placeholder = 'PILOT NAME';
    this.input.spellcheck = false;
    this.input.autocomplete = 'off';
    this.input.value = this.opts.initialName;
    this.input.addEventListener('input', () => {
      const valid = sanitizePilotName(this.input!.value) !== null;
      this.playBtn!.disabled = !valid;
      if (valid) this.setHintVisible(false);
    });

    this.hint = document.createElement('div');
    this.hint.className = 'sb-entry-hint';
    this.hint.textContent = HINT_TEXT;

    this.playBtn = document.createElement('button');
    this.playBtn.className = 'sb-entry-play';
    this.playBtn.type = 'button';
    this.playBtn.textContent = 'PLAY';
    this.playBtn.disabled = sanitizePilotName(this.opts.initialName) === null;
    this.playBtn.addEventListener('click', () => this.submit());

    const goal = document.createElement('div');
    goal.className = 'sb-entry-goal';
    goal.textContent = GOAL_TEXT;
    this.seasonEl = document.createElement('div');
    this.seasonEl.className = 'sb-entry-season';
    const controls = document.createElement('div');
    controls.className = 'sb-entry-controls';
    controls.textContent = CONTROLS_TEXT;

    this.root.append(logo, subtitle, goal, this.seasonEl, this.input, this.hint, this.playBtn, controls);
    document.body.appendChild(this.root);

    document.addEventListener('keydown', this.onKeydownCapture, true);
  }

  /** PLAY path (button click or Enter): sanitize → onPlay + hide, or reject. */
  private submit(): void {
    if (this.destroyed || this.input === null) return;
    const sanitized = sanitizePilotName(this.input.value);
    if (sanitized === null) {
      this.setHintVisible(true);
      this.shakeInput();
      return;
    }
    this.hide();
    this.opts.onPlay(sanitized);
  }

  private setHintVisible(shown: boolean): void {
    this.hint?.classList.toggle('sb-entry-hint--visible', shown);
  }

  /** Restartable 300ms shake on the input (invalid PLAY attempt). */
  private shakeInput(): void {
    const input = this.input;
    if (input === null) return;
    if (this.shakeTimer !== null) clearTimeout(this.shakeTimer);
    input.classList.remove('sb-entry-shake');
    // Force a reflow so re-adding the class restarts the animation even
    // when the previous shake is still mid-flight.
    void input.offsetWidth;
    input.classList.add('sb-entry-shake');
    this.shakeTimer = setTimeout(() => {
      input.classList.remove('sb-entry-shake');
      this.shakeTimer = null;
    }, 300);
  }
}
